import {
  compareHuge,
  huge,
  hugeZero,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";

const HOV_CORE_RATE_MS_PER_HOUR = 3_600_000;

export interface RecentHovCoreRateTracker {
  observe(nowMs: number, currentHovCore: HugeNumberInput): void;
  reset(currentHovCore: HugeNumberInput, observedAtMs: number): void;
  getRate(nowMs: number): HugeNumber;
}

export function createRecentHovCoreRateTracker(
  initialHovCore: HugeNumberInput,
  initialObservedAtMs: number,
): RecentHovCoreRateTracker {
  assertFiniteMilliseconds(initialObservedAtMs, "initialObservedAtMs");

  let lastObservedHovCore = huge(initialHovCore);
  let lastObservedAtMs = initialObservedAtMs;
  let lastRecentHovCoreGainDelta: HugeNumber | null = null;
  let lastRecentHovCoreGainDurationMs = 0;
  let lastRecentHovCoreGainObservedAtMs = initialObservedAtMs;

  const updateObservedBaseline = (
    currentHovCore: HugeNumberInput,
    observedAtMs: number,
  ): void => {
    assertFiniteMilliseconds(observedAtMs, "observedAtMs");
    lastObservedHovCore = huge(currentHovCore);
    lastObservedAtMs = observedAtMs;
  };

  const updateRecentGainSample = (
    delta: HugeNumber,
    elapsedMs: number,
    observedAtMs: number,
  ): void => {
    assertFiniteMilliseconds(elapsedMs, "elapsedMs");
    assertFiniteMilliseconds(observedAtMs, "observedAtMs");
    const durationMs =
      lastRecentHovCoreGainDelta === null
        ? elapsedMs + HOV_CORE_RATE_MS_PER_HOUR
        : elapsedMs;
    lastRecentHovCoreGainDelta = delta;
    lastRecentHovCoreGainDurationMs = durationMs;
    lastRecentHovCoreGainObservedAtMs = observedAtMs;
  };

  return {
    observe(nowMs: number, currentHovCore: HugeNumberInput): void {
      assertFiniteMilliseconds(nowMs, "nowMs");
      const current = huge(currentHovCore);
      const delta = current.sub(lastObservedHovCore);
      const comparison = compareHuge(delta, 0);

      if (comparison > 0) {
        const elapsedMs = nowMs - lastObservedAtMs;
        if (elapsedMs > 0) {
          updateRecentGainSample(delta, elapsedMs, nowMs);
        }
        updateObservedBaseline(current, nowMs);
        return;
      }

      if (comparison < 0) {
        updateObservedBaseline(current, nowMs);
      }
    },

    reset(currentHovCore: HugeNumberInput, observedAtMs: number): void {
      updateObservedBaseline(currentHovCore, observedAtMs);
      lastRecentHovCoreGainDelta = null;
      lastRecentHovCoreGainDurationMs = 0;
      lastRecentHovCoreGainObservedAtMs = observedAtMs;
    },

    getRate(nowMs: number): HugeNumber {
      assertFiniteMilliseconds(nowMs, "nowMs");
      if (
        lastRecentHovCoreGainDelta === null ||
        lastRecentHovCoreGainDurationMs <= 0
      ) {
        return hugeZero();
      }

      const agedHours = Math.max(
        0,
        Math.floor(
          (nowMs - lastRecentHovCoreGainObservedAtMs) /
            HOV_CORE_RATE_MS_PER_HOUR,
        ),
      );
      const effectiveDurationMs =
        lastRecentHovCoreGainDurationMs + agedHours * HOV_CORE_RATE_MS_PER_HOUR;

      return lastRecentHovCoreGainDelta
        .mul(HOV_CORE_RATE_MS_PER_HOUR)
        .div(effectiveDurationMs);
    },
  };
}

function assertFiniteMilliseconds(value: number, name: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`${name} must be a finite millisecond value`);
  }
}
